import type { SupportTicket, TicketDetail, TicketStatus } from '../types/support';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

class SupportService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (options.headers) Object.assign(headers, options.headers as Record<string, string>);

    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail || 'Request failed');
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  async listTickets(skip = 0, limit = 20): Promise<SupportTicket[]> {
    return this.request(`/api/v1/support/tickets?skip=${skip}&limit=${limit}`);
  }

  async createTicket(subject: string, body: string): Promise<SupportTicket> {
    return this.request('/api/v1/support/tickets', {
      method: 'POST',
      body: JSON.stringify({ subject, body }),
    });
  }

  async getTicket(ticketId: number): Promise<TicketDetail> {
    return this.request(`/api/v1/support/tickets/${ticketId}`);
  }

  async addMessage(ticketId: number, body: string): Promise<{ id: number }> {
    return this.request(`/api/v1/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  }

  // Admin endpoints
  async adminListTickets(status?: TicketStatus): Promise<SupportTicket[]> {
    const params = new URLSearchParams({ limit: '200' });
    if (status) params.set('status', status);
    return this.request(`/api/v1/admin/support/tickets?${params.toString()}`);
  }

  async adminGetTicket(ticketId: number): Promise<TicketDetail> {
    return this.request(`/api/v1/admin/support/tickets/${ticketId}`);
  }

  async adminReply(ticketId: number, body: string): Promise<{ id: number; ticket_status: string }> {
    return this.request(`/api/v1/admin/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  }

  async changeTicketStatus(ticketId: number, status: TicketStatus): Promise<SupportTicket> {
    return this.request(`/api/v1/admin/support/tickets/${ticketId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }
}

export const supportService = new SupportService();
